import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    Box,
    Button,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    Grid,
    Chip
} from '@material-ui/core';
import {Alert, Autocomplete} from '@material-ui/lab';
import {
    CheckCircleOutline,
    FilterList,
    GetApp,
    KeyboardArrowDown,
    KeyboardArrowUp,
    PersonAdd,
    RemoveCircle,
    Clear,
    ExpandMore,
    ExpandLess
} from '@material-ui/icons';
import auditDataStore from '../../stores/license/auditDataStore';
import LinearProgressWithLabel from '../../components/LinearProgress/LinearProgressWithLabel';
import XLSX from 'xlsx-js-style';

interface ReviewFilters {
    managerName: string;
    managerEmail: string;
    comment: string;
    status: 'all' | 'completed' | 'pending';
    employeeName: string;
    employeeEmail: string;
    employeeTitle: string;
    actionText: string;
    selectedOptions: string;
}

const ReviewStats: React.FC = observer(() => {
    const [selectedTab, setSelectedTab] = useState(0);
    const [openManagerRows, setOpenManagerRows] = useState<{ [key: string]: boolean }>({});
    const [openEmployeeRows, setOpenEmployeeRows] = useState<{ [key: string]: boolean }>({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState<string>('managerName');
    const [selectedDepartment, setSelectedDepartment] = useState<string>('All');
    const [exportingEmails, setExportingEmails] = useState(false);
    const [grantAccessDialogOpen, setGrantAccessDialogOpen] = useState(false);
    const [selectedFormForAccess, setSelectedFormForAccess] = useState<any>(null);
    const [selectedManager, setSelectedManager] = useState<any>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('info');
    const [existingPermissions, setExistingPermissions] = useState<any[]>([]);
    const [loadingPermissions, setLoadingPermissions] = useState(false);
    const [confirmRevokeDialogOpen, setConfirmRevokeDialogOpen] = useState(false);
    const [accessToRevoke, setAccessToRevoke] = useState<string>('');
    const [managerToRevoke, setManagerToRevoke] = useState<string>('');
    const [revokingAccess, setRevokingAccess] = useState(false);

    const [filters, setFilters] = useState<ReviewFilters>({
        managerName: '',
        managerEmail: '',
        comment: '',
        status: 'all',
        employeeName: '',
        employeeEmail: '',
        employeeTitle: '',
        actionText: '',
        selectedOptions: ''
    });
    const [filtersExpanded, setFiltersExpanded] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            await auditDataStore.fetchAuditData();
            if (auditDataStore.auditData.length > 0) {
                // Find the most recent audit (latest startDate)
                // At initial load, selectedDepartment is 'All', so use all audit data
                const audits = auditDataStore.auditData;
                const mostRecentAuditIndex = audits.reduce((maxIndex, audit, index, array) => {
                    return new Date(audit.startDate) > new Date(array[maxIndex].startDate) ? index : maxIndex;
                }, 0);
                
                setSelectedTab(mostRecentAuditIndex);
                await auditDataStore.handleTabChange(mostRecentAuditIndex);
            }
        };
        fetchData();
    }, []);

    const getDepartments = (): string[] => {
        const departments = auditDataStore.auditData
            .map(audit => audit.department)
            .filter((value, index, self) => value && self.indexOf(value) === index);
        return ['All', ...departments];
    };

    const handleDepartmentChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setSelectedDepartment(event.target.value as string);
        
        // After changing department, select the most recent audit for that department
        const filteredAudits = event.target.value === 'All' 
            ? auditDataStore.auditData 
            : auditDataStore.auditData.filter(audit => audit.department === event.target.value);
            
        if (filteredAudits.length > 0) {
            // Find the most recent audit (latest startDate)
            const mostRecentAuditIndex = filteredAudits.reduce((maxIndex, audit, index, array) => {
                return new Date(audit.startDate) > new Date(array[maxIndex].startDate) ? index : maxIndex;
            }, 0);
            
            setSelectedTab(mostRecentAuditIndex);
            
            // Get the original index in the full audit data array
            const selectedAuditId = filteredAudits[mostRecentAuditIndex].id;
            const originalIndex = auditDataStore.auditData.findIndex(audit => audit.id === selectedAuditId);
            auditDataStore.handleTabChange(originalIndex);
        }
    };

    const getFilteredAudits = () => {
        if (selectedDepartment === 'All') {
            return auditDataStore.auditData;
        } else {
            return auditDataStore.auditData.filter(audit => audit.department === selectedDepartment);
        }
    };

    const handleTabChange = async (_: React.ChangeEvent<{}>, newValue: number) => {
        setSelectedTab(newValue);
        const filteredAudits = getFilteredAudits();
        if (filteredAudits.length > newValue) {
            const selectedAuditId = filteredAudits[newValue].id;
            const originalIndex = auditDataStore.auditData.findIndex(audit => audit.id === selectedAuditId);
            await auditDataStore.handleTabChange(originalIndex);
        }
    };

    const handleManagerRowClick = (id: string) => {
        setOpenManagerRows((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleEmployeeRowClick = (id: string) => {
        setOpenEmployeeRows((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getActionText = (actionId: string) => {
        const action = auditDataStore.auditStats?.licenseAuditActions.find(a => a.id === actionId);
        return action ? action.question : 'Unknown Action';
    };

    const getOptionText = (optionId: string) => {
        const option = auditDataStore.auditStats?.licenseAuditActions
            .flatMap(a => a.licenseActionOptions)
            .find(o => o.id === optionId);
        return option ? option.option : 'Unknown Option';
    };

    const handleFilterChange = (filterKey: keyof ReviewFilters, value: string) => {
        setFilters(prev => ({
            ...prev,
            [filterKey]: value
        }));
        setPage(0);
    };

    const clearAllFilters = () => {
        setFilters({
            managerName: '',
            managerEmail: '',
            comment: '',
            status: 'all',
            employeeName: '',
            employeeEmail: '',
            employeeTitle: '',
            actionText: '',
            selectedOptions: ''
        });
        setPage(0);
    };

    const hasActiveFilters = () => {
        return Object.entries(filters).some(([key, value]) => 
            key !== 'status' ? value !== '' : value !== 'all'
        );
    };

    const handleRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const allActionsHaveSelectedOptions = (form: any) => {
        return form.licenseReviews.every((review: any) =>
            review.licenseReviewActions.every((action: any) => action.selectedOptions.length > 0)
        );
    };

    // Enhanced filtering function
    const getFilteredForms = () => {
        const rawForms = auditDataStore.auditStats?.licenseManagerForms || [];
        
        return rawForms.filter(form => {
            // Manager-level filters
            const managerNameMatch = !filters.managerName || 
                form.managerName.toLowerCase().includes(filters.managerName.toLowerCase());
            const managerEmailMatch = !filters.managerEmail || 
                form.managerEmail.toLowerCase().includes(filters.managerEmail.toLowerCase());
            const commentMatch = !filters.comment || 
                (form.comment && form.comment.toLowerCase().includes(filters.comment.toLowerCase()));
            
            // Status filter
            const isCompleted = allActionsHaveSelectedOptions(form);
            const statusMatch = filters.status === 'all' || 
                (filters.status === 'completed' && isCompleted) ||
                (filters.status === 'pending' && !isCompleted);
            
            // Employee-level filters (check if any employee matches)
            let employeeMatch = true;
            if (filters.employeeName || filters.employeeEmail || filters.employeeTitle || 
                filters.actionText || filters.selectedOptions) {
                
                employeeMatch = form.licenseReviews.some((review: any) => {
                    const nameMatch = !filters.employeeName || 
                        review.name.toLowerCase().includes(filters.employeeName.toLowerCase());
                    const emailMatch = !filters.employeeEmail || 
                        review.email.toLowerCase().includes(filters.employeeEmail.toLowerCase());
                    const titleMatch = !filters.employeeTitle || 
                        review.title.toLowerCase().includes(filters.employeeTitle.toLowerCase());
                    
                    // Action-level filters
                    let actionMatch = true;
                    if (filters.actionText || filters.selectedOptions) {
                        actionMatch = review.licenseReviewActions.some((action: any) => {
                            const actionTextMatch = !filters.actionText || 
                                getActionText(action.actionId).toLowerCase().includes(filters.actionText.toLowerCase());
                            const optionsMatch = !filters.selectedOptions || 
                                action.selectedOptions.some((optionId: string) => 
                                    getOptionText(optionId).toLowerCase().includes(filters.selectedOptions.toLowerCase())
                                );
                            return actionTextMatch && optionsMatch;
                        });
                    }
                    
                    return nameMatch && emailMatch && titleMatch && actionMatch;
                });
            }
            
            return managerNameMatch && managerEmailMatch && commentMatch && statusMatch && employeeMatch;
        });
    };

    const filteredForms = getFilteredForms();

    const sortedForms = filteredForms.sort((a, b) => {
        if (orderBy === 'managerName') {
            return (a.managerName < b.managerName ? -1 : 1) * (order === 'asc' ? 1 : -1);
        } else if (orderBy === 'managerEmail') {
            return (a.managerEmail < b.managerEmail ? -1 : 1) * (order === 'asc' ? 1 : -1);
        } else if (orderBy === 'comment') {
            const aComment = a.comment || '';
            const bComment = b.comment || '';
            return (aComment < bComment ? -1 : 1) * (order === 'asc' ? 1 : -1);
        } else if (orderBy === 'status') {
            const aStatus = allActionsHaveSelectedOptions(a) ? 'Completed' : 'Pending';
            const bStatus = allActionsHaveSelectedOptions(b) ? 'Completed' : 'Pending';
            return (aStatus < bStatus ? -1 : 1) * (order === 'asc' ? 1 : -1);
        } else {
            return 0;
        }
    });

    const handleExport = () => {
        const wb = XLSX.utils.book_new();

        const wsData: (string | object)[][] = [];

        const merges: any[] = [];

        const headerStyle = {
            font: { bold: true },
            alignment: { horizontal: 'left', vertical: 'center' },
            fill: { fgColor: { rgb: 'FFD700' } },
        };

        // Use filtered forms for export
        filteredForms.forEach((form, _) => {
            wsData.push([
                { v: `Manager: ${form.managerName}`, s: { font: { bold: true } } },
                { v: `Email: ${form.managerEmail}`, s: { font: { bold: true } } },
                { v: `Comment: ${form.comment || ''}`, s: { font: { bold: true } } },
            ]);

            if (form.licenseReviews && form.licenseReviews.length > 0) {
                form.licenseReviews.forEach((review: any) => {

                    wsData.push([
                        { v: 'Name', s: headerStyle },
                        { v: 'Email', s: headerStyle },
                        '',
                    ]);

                    wsData.push([
                        `${review.name}`,
                        `${review.email}`,
                        '',
                    ]);

                    wsData.push([
                        { v: 'Action', s: headerStyle },
                        { v: 'Selected Options', s: headerStyle },
                        '',
                    ]);

                    review.licenseReviewActions?.forEach((action: any) => {
                        const selectedOptionsText = action.selectedOptions.map(getOptionText).join(', ');
                        wsData.push([
                            `${getActionText(action.actionId)}`,
                            `${selectedOptionsText}`,
                            '',
                        ]);
                    });
                });
            } else {
                wsData.push(['No License Reviews', '', '']);
            }

            wsData.push(['', '', '']);
        });

        const ws = XLSX.utils.aoa_to_sheet(wsData);

        ws['!merges'] = merges;

        ws['!cols'] = [
            { wch: 35 },
            { wch: 35 },
            { wch: 35 },
        ];

        XLSX.utils.book_append_sheet(wb, ws, 'License Reviews');

        XLSX.writeFile(wb, 'License_Reviews_Export.xlsx');
    };

    const exportIncompleteReviewsManagerEmails = async () => {
        setExportingEmails(true);
        try {
            // Use filtered forms to get incomplete reviews that match current filters
            const incompleteManagerEmails = filteredForms
                .filter(form => !allActionsHaveSelectedOptions(form))
                .map(form => form.managerEmail);
            
            if (incompleteManagerEmails.length > 0) {
                // Create TXT content with emails separated by semicolons
                const txtContent = incompleteManagerEmails.join('; ');
                
                // Create and download TXT file
                const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', 'Incomplete_Reviews_Manager_Emails.txt');
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                console.log('No incomplete reviews found matching current filters');                
            }
        } catch (error) {
            console.error('Error exporting incomplete review manager emails:', error);            
        } finally {
            setExportingEmails(false);
        }
    };

    const handleGrantAccess = async (form: any) => {
        setSelectedFormForAccess(form);
        setSelectedManager(null);
        setExistingPermissions([]);
        setGrantAccessDialogOpen(true);
        
        // Load existing permissions
        setLoadingPermissions(true);
        try {
            const result = await auditDataStore.getLicenseFormAccessPermissions(form.id);
            if (result.success) {
                setExistingPermissions(result.data);
            }
        } catch (error) {
            console.error('Failed to load permissions:', error);
        } finally {
            setLoadingPermissions(false);
        }
    };

    const handleGrantAccessSubmit = async () => {
        if (!selectedFormForAccess || !selectedManager) {
            setSnackbarMessage('Please select a manager');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        try {
            const result = await auditDataStore.grantFormAccess({
                licenseFormId: selectedFormForAccess.id,
                managerEmail: selectedManager.managerEmail,
                managerName: selectedManager.managerName,
                adminEmail: '' // Will be set by the backend from auth context
            });

            setSnackbarMessage(result.message);
            setSnackbarSeverity(result.severity as 'success' | 'error' | 'info');
            setSnackbarOpen(true);

            if (result.success) {
                // Refresh permissions
                const updatedResult = await auditDataStore.getLicenseFormAccessPermissions(selectedFormForAccess.id);
                if (updatedResult.success) {
                    setExistingPermissions(updatedResult.data);
                }
                setSelectedManager(null);
                // Refresh the audit data
                await auditDataStore.handleTabChange(selectedTab);
            }
        } catch (error) {
            setSnackbarMessage('Error granting access');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleRevokeAccess = (accessId: string) => {
        // Find the manager name for this access permission
        const permission = existingPermissions.find(p => p.id === accessId);
        const managerName = permission ? getManagerNameByEmail(permission.grantedToManagerEmail) : 'this manager';
        
        setAccessToRevoke(accessId);
        setManagerToRevoke(managerName);
        setConfirmRevokeDialogOpen(true);
    };

    const handleConfirmRevokeAccess = async () => {
        setRevokingAccess(true);
        try {
            const result = await auditDataStore.revokeFormAccess({
                accessId: accessToRevoke,
                adminEmail: '' // Will be set by the backend from auth context
            });

            setSnackbarMessage(result.message);
            setSnackbarSeverity(result.severity as 'success' | 'error' | 'info');
            setSnackbarOpen(true);

            if (result.success) {
                // Refresh permissions
                const updatedResult = await auditDataStore.getLicenseFormAccessPermissions(selectedFormForAccess.id);
                if (updatedResult.success) {
                    setExistingPermissions(updatedResult.data);
                }
                // Refresh the audit data
                await auditDataStore.handleTabChange(selectedTab);
            }
        } catch (error) {
            setSnackbarMessage('Error revoking access');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setRevokingAccess(false);
            setConfirmRevokeDialogOpen(false);
            setAccessToRevoke('');
            setManagerToRevoke('');
        }
    };

    const handleCancelRevokeAccess = () => {
        setConfirmRevokeDialogOpen(false);
        setAccessToRevoke('');
        setManagerToRevoke('');
        setRevokingAccess(false);
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const getAvailableManagers = () => {
        const grantedEmails = existingPermissions.map(p => p.grantedToManagerEmail);
        const allForms = auditDataStore.auditStats?.licenseManagerForms || [];
        return allForms.filter(form => 
            form.managerEmail !== selectedFormForAccess?.managerEmail &&
            !grantedEmails.includes(form.managerEmail)
        );
    };

    const getManagerNameByEmail = (email: string) => {
        const allForms = auditDataStore.auditStats?.licenseManagerForms || [];
        const manager = allForms.find(form => form.managerEmail === email);
        return manager ? manager.managerName : email;
    };

    return (
        <Box>
            <Typography variant="h4" component="h1" gutterBottom>
                License Reviews
            </Typography>
            <Typography variant="body1" gutterBottom>
                This page is used to list license reviews. You can preview the license reviews.
            </Typography>
            <hr />
            {auditDataStore.loading && <CircularProgress />}
            {auditDataStore.fetchError && (
                <Typography variant="body1" color="error" gutterBottom>
                    {auditDataStore.fetchError}
                </Typography>
            )}
            {!auditDataStore.selectedAudit && !auditDataStore.fetchError && (
                <Typography variant="body1" color="textSecondary" gutterBottom>
                    No review process is currently in progress.
                </Typography>
            )}

            {auditDataStore.auditData.length > 0 && (
                <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
                    <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                        <InputLabel id="department-filter-label">Filter by Department</InputLabel>
                        <Select
                            labelId="department-filter-label"
                            id="department-filter"
                            value={selectedDepartment}
                            onChange={handleDepartmentChange}
                            label="Filter by Department"
                        >
                            {getDepartments().map(department => (
                                <MenuItem key={department} value={department}>
                                    {department}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <Tooltip title="Export Incomplete Reviews Manager Emails to TXT (filtered results)">
                        <IconButton
                            onClick={exportIncompleteReviewsManagerEmails}
                            disabled={exportingEmails}
                            color="primary"
                        >
                            {exportingEmails ? <CircularProgress size={24} /> : <GetApp />}
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {auditDataStore.selectedAudit && (
                <>
                    <Tabs value={selectedTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                        {getFilteredAudits().map((audit) => (
                            <Tab
                                key={audit.id}
                                label={`${new Date(audit.startDate).toLocaleString('default', {
                                    month: 'short',
                                    year: 'numeric',
                                })}${selectedDepartment === 'All' && audit.department ? ` - ${audit.department}` : ''}`}
                            />
                        ))}
                    </Tabs>

                    <Box p={3}>
                        <Typography variant="h6">Audit Details</Typography>
                        {auditDataStore.selectedAudit.department && (
                            <Typography>
                                Department: {auditDataStore.selectedAudit.department}
                            </Typography>
                        )}
                        <Typography>
                            Start Date:{' '}
                            {new Date(auditDataStore.selectedAudit.startDate).toLocaleDateString()}
                        </Typography>
                        <Typography>
                            Reviews Progress:{' '}
                            {`${auditDataStore.selectedAudit.reviewsCompleted} / ${auditDataStore.selectedAudit.totalReviews}`}
                        </Typography>
                        <LinearProgressWithLabel
                            value={
                                (auditDataStore.selectedAudit.reviewsCompleted /
                                    auditDataStore.selectedAudit.totalReviews) *
                                100
                            }
                        />

                        {auditDataStore.auditStats && (
                            <>
                                {/* Filter Section */}
                                <Paper variant="outlined" style={{ padding: '12px', marginBottom: '12px' }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                        <Box display="flex" alignItems="center">
                                            <FilterList fontSize="small" style={{ marginRight: '6px', color: '#666' }} />
                                            <Typography variant="subtitle1" style={{ fontWeight: 500 }}>Filters</Typography>
                                            {hasActiveFilters() && (
                                                <Chip
                                                    label="Active"
                                                    color="primary"
                                                    size="small"
                                                    style={{ marginLeft: '8px', height: '20px', fontSize: '0.7rem' }}
                                                />
                                            )}
                                        </Box>
                                        <Box>
                                            {hasActiveFilters() && (
                                                <Button
                                                    startIcon={<Clear fontSize="small" />}
                                                    onClick={clearAllFilters}
                                                    size="small"
                                                    style={{ marginRight: '6px', minWidth: 'auto', padding: '4px 8px' }}
                                                >
                                                    Clear
                                                </Button>
                                            )}
                                            <Button
                                                startIcon={filtersExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                onClick={() => setFiltersExpanded(!filtersExpanded)}
                                                size="small"
                                                style={{ minWidth: 'auto', padding: '4px 8px' }}
                                            >
                                                {filtersExpanded ? 'Less' : 'More'}
                                            </Button>
                                        </Box>
                                    </Box>
                                    
                                    {/* Always visible filters - more compact */}
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                label="Manager Name"
                                                variant="standard"
                                                size="small"
                                                fullWidth
                                                value={filters.managerName}
                                                onChange={(e) => handleFilterChange('managerName', e.target.value)}
                                                InputProps={{
                                                    style: { 
                                                        height: '32px',
                                                        backgroundColor: 'transparent'
                                                    }
                                                }}
                                                inputProps={{
                                                    style: { 
                                                        backgroundColor: 'transparent'
                                                    }
                                                }}
                                                InputLabelProps={{
                                                    shrink: true,
                                                    style: { 
                                                        fontSize: '0.875rem',
                                                        backgroundColor: 'transparent'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                label="Manager Email"
                                                variant="standard"
                                                size="small"
                                                fullWidth
                                                value={filters.managerEmail}
                                                onChange={(e) => handleFilterChange('managerEmail', e.target.value)}
                                                InputProps={{
                                                    style: { 
                                                        height: '32px',
                                                        backgroundColor: 'transparent'
                                                    }
                                                }}
                                                inputProps={{
                                                    style: { 
                                                        backgroundColor: 'transparent'
                                                    }
                                                }}
                                                InputLabelProps={{
                                                    shrink: true,
                                                    style: { 
                                                        fontSize: '0.875rem',
                                                        backgroundColor: 'transparent'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormControl variant="standard" size="small" fullWidth>
                                                <InputLabel 
                                                    shrink={true}
                                                    style={{ 
                                                        fontSize: '0.875rem',
                                                        backgroundColor: 'transparent'
                                                    }}
                                                >Status</InputLabel>
                                                <Select
                                                    value={filters.status}
                                                    onChange={(e) => handleFilterChange('status', e.target.value as string)}
                                                    style={{ 
                                                        height: '32px',
                                                        backgroundColor: 'transparent'
                                                    }}
                                                    inputProps={{
                                                        style: { 
                                                            backgroundColor: 'transparent'
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="all">All</MenuItem>
                                                    <MenuItem value="completed">Completed</MenuItem>
                                                    <MenuItem value="pending">Pending</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                label="Comment"
                                                variant="standard"
                                                size="small"
                                                fullWidth
                                                value={filters.comment}
                                                onChange={(e) => handleFilterChange('comment', e.target.value)}
                                                InputProps={{
                                                    style: { 
                                                        height: '32px',
                                                        backgroundColor: 'transparent'
                                                    }
                                                }}
                                                inputProps={{
                                                    style: { 
                                                        backgroundColor: 'transparent'
                                                    }
                                                }}
                                                InputLabelProps={{
                                                    shrink: true,
                                                    style: { 
                                                        fontSize: '0.875rem',
                                                        backgroundColor: 'transparent'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                    
                                    {/* Expandable filters */}
                                    <Collapse in={filtersExpanded}>
                                        <Box mt={1.5}>
                                            <Typography variant="caption" display="block" gutterBottom color="textSecondary" style={{ fontWeight: 500, marginBottom: '8px' }}>
                                                Employee & Action Filters
                                            </Typography>
                                            <Grid container spacing={1}>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <TextField
                                                        label="Employee Name"
                                                        variant="standard"
                                                        size="small"
                                                        fullWidth
                                                        value={filters.employeeName}
                                                        onChange={(e) => handleFilterChange('employeeName', e.target.value)}
                                                        InputProps={{
                                                            style: { 
                                                                height: '32px',
                                                                backgroundColor: 'transparent'
                                                            }
                                                        }}
                                                        inputProps={{
                                                            style: { 
                                                                backgroundColor: 'transparent'
                                                            }
                                                        }}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                            style: { 
                                                                fontSize: '0.875rem',
                                                                backgroundColor: 'transparent'
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <TextField
                                                        label="Employee Email"
                                                        variant="standard"
                                                        size="small"
                                                        fullWidth
                                                        value={filters.employeeEmail}
                                                        onChange={(e) => handleFilterChange('employeeEmail', e.target.value)}
                                                        InputProps={{
                                                            style: { 
                                                                height: '32px',
                                                                backgroundColor: 'transparent'
                                                            }
                                                        }}
                                                        inputProps={{
                                                            style: { 
                                                                backgroundColor: 'transparent'
                                                            }
                                                        }}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                            style: { 
                                                                fontSize: '0.875rem',
                                                                backgroundColor: 'transparent'
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <TextField
                                                        label="Employee Title"
                                                        variant="standard"
                                                        size="small"
                                                        fullWidth
                                                        value={filters.employeeTitle}
                                                        onChange={(e) => handleFilterChange('employeeTitle', e.target.value)}
                                                        InputProps={{
                                                            style: { 
                                                                height: '32px',
                                                                backgroundColor: 'transparent'
                                                            }
                                                        }}
                                                        inputProps={{
                                                            style: { 
                                                                backgroundColor: 'transparent'
                                                            }
                                                        }}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                            style: { 
                                                                fontSize: '0.875rem',
                                                                backgroundColor: 'transparent'
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <TextField
                                                        label="Action Text"
                                                        variant="standard"
                                                        size="small"
                                                        fullWidth
                                                        value={filters.actionText}
                                                        onChange={(e) => handleFilterChange('actionText', e.target.value)}
                                                        InputProps={{
                                                            style: { 
                                                                height: '32px',
                                                                backgroundColor: 'transparent'
                                                            }
                                                        }}
                                                        inputProps={{
                                                            style: { 
                                                                backgroundColor: 'transparent'
                                                            }
                                                        }}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                            style: { 
                                                                fontSize: '0.875rem',
                                                                backgroundColor: 'transparent'
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <TextField
                                                        label="Selected Options"
                                                        variant="standard"
                                                        size="small"
                                                        fullWidth
                                                        value={filters.selectedOptions}
                                                        onChange={(e) => handleFilterChange('selectedOptions', e.target.value)}
                                                        InputProps={{
                                                            style: { 
                                                                height: '32px',
                                                                backgroundColor: 'transparent'
                                                            }
                                                        }}
                                                        inputProps={{
                                                            style: { 
                                                                backgroundColor: 'transparent'
                                                            }
                                                        }}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                            style: { 
                                                                fontSize: '0.875rem',
                                                                backgroundColor: 'transparent'
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Collapse>
                                </Paper>

                                {/* Active Filters Display */}
                                {hasActiveFilters() && (
                                    <Box mb={2}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Active Filters:
                                        </Typography>
                                        <Box display="flex" flexWrap="wrap">
                                            {Object.entries(filters).map(([key, value]) => {
                                                if ((key === 'status' && value !== 'all') || (key !== 'status' && value !== '')) {
                                                    const displayKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                                                    const displayValue = key === 'status' ? value.charAt(0).toUpperCase() + value.slice(1) : value;
                                                    return (
                                                        <Chip
                                                            key={key}
                                                            label={`${displayKey}: ${displayValue}`}
                                                            onDelete={() => handleFilterChange(key as keyof ReviewFilters, key === 'status' ? 'all' : '')}
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                            style={{ marginRight: '8px', marginBottom: '4px' }}
                                                        />
                                                    );
                                                }
                                                return null;
                                            })}
                                        </Box>
                                    </Box>
                                )}

                                {/* Action Bar */}
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="body2" color="textSecondary">
                                        {filteredForms.length} result{filteredForms.length !== 1 ? 's' : ''} found
                                        {hasActiveFilters() && ' (filtered)'}
                                    </Typography>
                                    <Tooltip title="Export filtered results to Excel">
                                        <Button variant="contained" color="primary" onClick={handleExport}>
                                            Export to Excel
                                        </Button>
                                    </Tooltip>
                                </Box>

                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ width: '50px' }} />
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={orderBy === 'managerName'}
                                                        direction={orderBy === 'managerName' ? order : 'asc'}
                                                        onClick={() => handleRequestSort('managerName')}
                                                    >
                                                        Manager Name
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={orderBy === 'managerEmail'}
                                                        direction={orderBy === 'managerEmail' ? order : 'asc'}
                                                        onClick={() => handleRequestSort('managerEmail')}
                                                    >
                                                        Manager Email
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={orderBy === 'comment'}
                                                        direction={orderBy === 'comment' ? order : 'asc'}
                                                        onClick={() => handleRequestSort('comment')}
                                                    >
                                                        Comment
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell style={{ width: '50px' }}>
                                                    <TableSortLabel
                                                        active={orderBy === 'status'}
                                                        direction={orderBy === 'status' ? order : 'asc'}
                                                        onClick={() => handleRequestSort('status')}
                                                    >
                                                        Status
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {sortedForms
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((form) => (
                                                    <React.Fragment key={form.managerEmail}>
                                                        <TableRow>
                                                            <TableCell style={{ width: '50px' }}>
                                                                <IconButton onClick={() => handleManagerRowClick(form.managerEmail)}>
                                                                    {openManagerRows[form.managerEmail] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                                </IconButton>
                                                            </TableCell>
                                                            <TableCell>{form.managerName}</TableCell>
                                                            <TableCell>{form.managerEmail}</TableCell>
                                                            <TableCell>{form.comment}</TableCell>
                                                            <TableCell>
                                                                {allActionsHaveSelectedOptions(form) ? (
                                                                    <CheckCircleOutline style={{ color: 'green' }} />
                                                                ) : <></>}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Tooltip title="Grant Access to Another Manager">
                                                                    <IconButton
                                                                        onClick={() => handleGrantAccess(form)}
                                                                        color="primary"
                                                                        size="small"
                                                                    >
                                                                        <PersonAdd />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell
                                                                style={{ paddingBottom: 0, paddingTop: 0 }}
                                                                colSpan={6}
                                                            >
                                                                <Collapse
                                                                    in={openManagerRows[form.managerEmail]}
                                                                    timeout="auto"
                                                                    unmountOnExit
                                                                >
                                                                    <Box margin={1}>
                                                                        <Typography variant="h6" gutterBottom>
                                                                            License Reviews
                                                                        </Typography>
                                                                        <Table size="small">
                                                                            <TableHead>
                                                                                <TableRow>
                                                                                    <TableCell style={{ width: '50px' }} />
                                                                                    <TableCell>Name</TableCell>
                                                                                    <TableCell>Email</TableCell>
                                                                                    <TableCell>Title</TableCell>
                                                                                </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                                {form.licenseReviews && form.licenseReviews.length > 0 ? (
                                                                                    form.licenseReviews.map((review: any) => (
                                                                                        <React.Fragment key={review.email}>
                                                                                            <TableRow>
                                                                                                <TableCell style={{ width: '50px' }}>
                                                                                                    <IconButton
                                                                                                        onClick={() => handleEmployeeRowClick(review.email)}
                                                                                                    >
                                                                                                        {openEmployeeRows[review.email] ? (
                                                                                                            <KeyboardArrowUp />
                                                                                                        ) : (
                                                                                                            <KeyboardArrowDown />
                                                                                                        )}
                                                                                                    </IconButton>
                                                                                                </TableCell>
                                                                                                <TableCell>{review.name}</TableCell>
                                                                                                <TableCell>{review.email}</TableCell>
                                                                                                <TableCell>{review.title}</TableCell>
                                                                                            </TableRow>
                                                                                            <TableRow>
                                                                                                <TableCell
                                                                                                    style={{ paddingBottom: 0, paddingTop: 0 }}
                                                                                                    colSpan={3}
                                                                                                >
                                                                                                    <Collapse
                                                                                                        in={openEmployeeRows[review.email]}
                                                                                                        timeout="auto"
                                                                                                        unmountOnExit
                                                                                                    >
                                                                                                        <Box margin={1}>
                                                                                                            <Typography variant="h6" gutterBottom>
                                                                                                                Actions
                                                                                                            </Typography>
                                                                                                            <Table size="small">
                                                                                                                <TableHead>
                                                                                                                    <TableRow>
                                                                                                                        <TableCell>Action</TableCell>
                                                                                                                        <TableCell>Selected Options</TableCell>
                                                                                                                    </TableRow>
                                                                                                                </TableHead>
                                                                                                                <TableBody>
                                                                                                                    {review.licenseReviewActions.map((action: any) => (
                                                                                                                        <TableRow key={action.id}>
                                                                                                                            <TableCell>{getActionText(action.actionId)}</TableCell>
                                                                                                                            <TableCell>{action.selectedOptions.map(getOptionText).join(', ')}</TableCell>
                                                                                                                        </TableRow>
                                                                                                                    ))}
                                                                                                                </TableBody>
                                                                                                            </Table>
                                                                                                        </Box>
                                                                                                    </Collapse>
                                                                                                </TableCell>
                                                                                            </TableRow>
                                                                                        </React.Fragment>
                                                                                    ))
                                                                                ) : (
                                                                                    <TableRow>
                                                                                        <TableCell colSpan={3}>No License Reviews</TableCell>
                                                                                    </TableRow>
                                                                                )}
                                                                            </TableBody>
                                                                        </Table>
                                                                    </Box>
                                                                </Collapse>
                                                            </TableCell>
                                                        </TableRow>
                                                    </React.Fragment>
                                                ))}
                                        </TableBody>
                                    </Table>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25, 50]}
                                        component="div"
                                        count={filteredForms.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                </TableContainer>
                            </>
                        )}
                    </Box>
                </>
            )}

            {/* Grant Access Dialog */}
            <Dialog 
                open={grantAccessDialogOpen} 
                onClose={() => setGrantAccessDialogOpen(false)}
                maxWidth="sm" 
                fullWidth
            >
                <DialogTitle>Manage Form Access</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom>
                        Managing access for "{selectedFormForAccess?.managerName}'s" form.
                    </Typography>
                    
                    {/* Existing Permissions Section */}
                    <Box mt={2} mb={3}>
                        <Typography variant="h6" gutterBottom>
                            Current Access Permissions
                        </Typography>
                        {loadingPermissions ? (
                            <CircularProgress size={24} />
                        ) : existingPermissions.length > 0 ? (
                            <List dense>
                                {existingPermissions.map((permission) => (
                                    <ListItem key={permission.id}>
                                        <ListItemText
                                            primary={getManagerNameByEmail(permission.grantedToManagerEmail)}
                                            secondary={permission.grantedToManagerEmail}
                                        />
                                        <ListItemSecondaryAction>
                                            <Tooltip title="Revoke Access">
                                                <IconButton
                                                    edge="end"
                                                    aria-label="revoke"
                                                    onClick={() => handleRevokeAccess(permission.id)}
                                                    color="secondary"
                                                    size="small"
                                                >
                                                    <RemoveCircle />
                                                </IconButton>
                                            </Tooltip>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                No managers currently have access to this form.
                            </Typography>
                        )}
                    </Box>
                    
                    <Divider />
                    
                    {/* Grant New Access Section */}
                    <Box mt={3}>
                        <Typography variant="h6" gutterBottom>
                            Grant New Access
                        </Typography>
                        <Typography variant="body2" gutterBottom color="textSecondary">
                            Only managers who have their own forms can be granted access.
                        </Typography>
                        
                        <Autocomplete
                            value={selectedManager}
                            onChange={(_event, newValue) => {
                                setSelectedManager(newValue);
                            }}
                            options={getAvailableManagers()}
                            getOptionLabel={(option) => `${option.managerName} (${option.managerEmail})`}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Manager"
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    placeholder="Type to search managers..."
                                    InputLabelProps={{
                                        shrink: true,
                                        style: { backgroundColor: 'white', padding: '0 4px' }
                                    }}
                                    InputProps={{
                                        ...params.InputProps,
                                        style: { paddingTop: '8px' }
                                    }}
                                    style={{ 
                                        marginTop: '20px',
                                        marginBottom: '8px'
                                    }}
                                />
                            )}
                            renderOption={(option) => (
                                <div style={{ 
                                    padding: '8px 16px', 
                                    lineHeight: '1.5',
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word',
                                    minHeight: '48px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    <div>
                                        <div style={{ fontWeight: 500, marginBottom: '2px' }}>
                                            {option.managerName}
                                        </div>
                                        <div style={{ fontSize: '0.875rem', color: '#666' }}>
                                            {option.managerEmail}
                                        </div>
                                    </div>
                                </div>
                            )}
                            ListboxProps={{
                                style: {
                                    maxHeight: '200px',
                                    overflow: 'auto',
                                    padding: '4px 0'
                                }
                            }}
                            PaperComponent={({ children, ...other }) => (
                                <Paper {...other} style={{ marginTop: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                                    {children}
                                </Paper>
                            )}
                            noOptionsText={getAvailableManagers().length === 0 ? "No available managers" : "No managers found"}
                            disabled={getAvailableManagers().length === 0}
                            filterOptions={(options, { inputValue }) => {
                                return options.filter((option) =>
                                    option.managerName.toLowerCase().includes(inputValue.toLowerCase()) ||
                                    option.managerEmail.toLowerCase().includes(inputValue.toLowerCase())
                                );
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGrantAccessDialogOpen(false)} color="primary">
                        Close
                    </Button>
                    <Button 
                        onClick={handleGrantAccessSubmit} 
                        color="primary" 
                        variant="contained"
                        disabled={!selectedManager || getAvailableManagers().length === 0}
                    >
                        Grant Access
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Revoke Access Dialog */}
            <Dialog 
                open={confirmRevokeDialogOpen} 
                onClose={handleCancelRevokeAccess}
                maxWidth="xs" 
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        <RemoveCircle color="error" style={{ marginRight: '8px' }} />
                        Confirm Revoke Access
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Are you sure you want to revoke access for <strong>{managerToRevoke}</strong>?
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        This manager will no longer be able to access "{selectedFormForAccess?.managerName}'s" form.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCancelRevokeAccess} 
                        color="primary"
                        disabled={revokingAccess}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmRevokeAccess} 
                        color="secondary" 
                        variant="contained"
                        disabled={revokingAccess}
                        startIcon={revokingAccess ? <CircularProgress size={16} color="inherit" /> : <RemoveCircle />}
                    >
                        {revokingAccess ? 'Revoking...' : 'Revoke Access'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
});

export default ReviewStats;